import { ButtonBase, ButtonBaseProps } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { SvgIconTypeMap } from '@mui/material/SvgIcon';
import {
  css,
  styled as muiStyled,
  type Theme,
  useTheme,
} from '@mui/material/styles';
import React from 'react';

import { LEFT_NAV_ITEM_HEIGHT, LEFT_NAV_FONT_SIZE } from './_type';
import useLeftNavContext from './left-nav-context';

import {
  DesignToken,
  type DesignTokenSet,
} from '~/client/components/base/core';
import { ArrowRight } from '~/client/components/base/icon';
import { spc } from '~/client/components/base/spacer';
import Text from '~/client/components/base/text';
import Tooltip from '~/client/components/base/tooltip';
import { useThemeMode } from '~/client/contexts/theme-context';
import { useDesignTokens } from '~/client/hooks/use-design-tokens';

interface Props extends Omit<ButtonBaseProps, 'component'> {
  className?: string;
  /**
   * Icon to render on the left side of text
   */
  icon?: OverridableComponent<SvgIconTypeMap>;

  /**
   * Name of icon (or an icon element) to render on the right side of text
   */
  rightIcon?: 'arrow-right' | OverridableComponent<SvgIconTypeMap>;
  /**
   * Selected component has a colored background
   */
  selected?: boolean;
  /**
   * Custom component to render as (e.g., Link for navigation)
   * When provided, ButtonBase renders as this component instead of button
   */
  component?: React.ElementType;
  /**
   * Route destination for Link components
   */
  to?: string;
  /**
   * URL for anchor elements
   */
  href?: string;
}

const LeftNavMenuItemImpl = React.forwardRef<HTMLElement, Props>(
  (
    { className, icon, rightIcon, children, component, to, href, ...props },
    ref
  ) => {
    const { state } = useLeftNavContext();
    const { navColumnOpen } = state;

    const Icon = icon;

    // Icon to render on right side of text
    let RightIcon: OverridableComponent<SvgIconTypeMap> | undefined = undefined;

    if (rightIcon === 'arrow-right') {
      RightIcon = ArrowRight;
    } else if (typeof rightIcon !== 'string') {
      RightIcon = rightIcon;
    }

    // Extract first letter for avatar when no icon
    const getFirstLetter = (content: React.ReactNode): string => {
      if (typeof content === 'string') {
        return content.charAt(0).toUpperCase();
      }
      // Handle React elements - try to extract text
      if (React.isValidElement(content)) {
        const textContent = extractTextFromElement(content);
        return textContent ? textContent.charAt(0).toUpperCase() : 'N';
      }
      return 'N';
    };

    const extractTextFromElement = (element: React.ReactElement): string => {
      if (typeof element.props?.children === 'string') {
        return element.props.children;
      }
      if (Array.isArray(element.props?.children)) {
        const text = element.props.children.find(
          (child: any) => typeof child === 'string'
        );
        return text || '';
      }
      return '';
    };

    const firstLetter = !Icon && children ? getFirstLetter(children) : '';

    // Build props for ButtonBase, conditionally including component/to/href
    // This allows ButtonBase to render as a Link when component is provided
    const buttonBaseProps = {
      ref: ref as React.Ref<HTMLButtonElement>,
      className: navColumnOpen ? 'menu-item nav-open' : 'menu-item',
      sx: {
        px: spc.s,
        py: spc.xs,
        mb: 0,
      },
      ...(component ? { component } : {}),
      ...(to ? { to } : {}),
      ...(href ? { href } : {}),
      ...props,
    };

    return (
      <div className={className}>
        <ButtonBase {...(buttonBaseProps as any)}>
          {Icon && (
            <Tooltip
              title={children}
              arrow
              disableHoverListener={navColumnOpen}
            >
              <Icon
                className="icon"
                sx={{
                  fontSize: navColumnOpen ? 24 : 28,
                  pr: navColumnOpen ? spc.xxs : 0,
                }}
              />
            </Tooltip>
          )}
          {!Icon && !navColumnOpen && children && (
            <Tooltip title={children} arrow placement="right">
              <div className="letter-avatar">{firstLetter}</div>
            </Tooltip>
          )}
          {navColumnOpen && children != null && (
            <div className="menu-item-content">
              {typeof children === 'string' ? (
                <Text sz="body16" noWrap align="left" color="inherit">
                  {children}
                </Text>
              ) : (
                children
              )}
            </div>
          )}
          {navColumnOpen && RightIcon && (
            <RightIcon
              className="right-icon"
              sx={{ fontSize: 24, pl: spc.xxs }}
            />
          )}
        </ButtonBase>
      </div>
    );
  }
);

interface LeftNavMenuItemStyledProps {
  theme?: Theme;
  selected?: boolean;
  tokens?: DesignTokenSet;
  isDark?: boolean;
}

const LeftNavMenuItemStyled = muiStyled(LeftNavMenuItemImpl, {
  shouldForwardProp: (prop) => prop !== 'tokens' && prop !== 'isDark',
})<LeftNavMenuItemStyledProps>(({ theme, selected, tokens, isDark }) => {
  const t = tokens ?? DesignToken;
  const nav = t.nav;

  const { disabledOpacity, activatedOpacity } = theme?.palette?.action ?? {
    disabledOpacity: 0.38,
    activatedOpacity: 0.12,
  };

  // Expanded sidebar: selected/hover use main left nav colour (teal), not light surface.
  const defaultTextColor = nav.itemTextOnDark;
  const hoverTextColor = nav.itemTextOnDarkHover;

  // Teal background for selected/hover (matches main left nav; dark mode uses subtle overlay)
  const tealBg = `var(--org-primary-color, ${t.brand.primary})`;
  const activeBgColor = isDark
    ? `color-mix(in srgb, var(--org-primary-color, ${nav.activeIndicator}) 15%, transparent)`
    : tealBg;
  const hoverBgColor = isDark
    ? `color-mix(in srgb, var(--org-primary-color, ${nav.activeIndicator}) 8%, transparent)`
    : tealBg;

  // Active indicator bar: bright accent on dark sidebar
  const barColor = `var(--org-primary-color, ${nav.activeIndicator})`;

  const textColorClass = selected
    ? `var(--org-primary-contrast, ${hoverTextColor})`
    : defaultTextColor;

  return css`
    /* Target ButtonBase with high specificity so we override MUI theme hover/selected */
    .menu-item.MuiButtonBase-root {
      width: 100%;
      position: relative;

      display: flex;
      align-items: center;
      justify-content: center;

      &.nav-open {
        justify-content: flex-start;
      }
      background-color: ${selected ? activeBgColor : 'transparent'} !important;
      border-radius: ${t.radiusSmall}px;

      height: ${LEFT_NAV_ITEM_HEIGHT}px;

      color: ${selected
        ? `var(--org-primary-contrast, ${hoverTextColor})`
        : defaultTextColor};

      transition:
        background-color 0.2s ease,
        color 0.2s ease;

      /* 3px left indicator bar for active state */
      ${selected &&
      css`
        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 3px;
          background-color: ${barColor};
          border-radius: 0;
        }
      `}

      .MuiTypography-root {
        color: ${textColorClass} !important;
        font-weight: ${selected ? 500 : 400};
        font-size: ${LEFT_NAV_FONT_SIZE}px;
        line-height: 1.4;
      }

      :disabled {
        opacity: ${disabledOpacity};
      }

      /* Hover: subtle light overlay on dark sidebar (override MUI theme) */
      :hover {
        background-color: ${selected ? activeBgColor : hoverBgColor} !important;
        color: ${selected
          ? `var(--org-primary-contrast, ${hoverTextColor})`
          : hoverTextColor};
        .MuiTypography-root {
          color: ${selected
            ? `var(--org-primary-contrast, ${hoverTextColor})`
            : hoverTextColor};
        }
      }

      :active {
        opacity: ${isDark ? 1 : activatedOpacity};
        color: ${selected
          ? `var(--org-primary-contrast, ${hoverTextColor})`
          : hoverTextColor};
        .MuiTypography-root {
          color: ${selected
            ? `var(--org-primary-contrast, ${hoverTextColor})`
            : hoverTextColor};
        }
      }

      .icon {
        flex-shrink: 0;
        color: inherit;
      }

      .letter-avatar {
        width: 28px;
        height: 28px;
        border-radius: ${t.radiusPill}px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 600;
        background-color: ${selected
          ? activeBgColor
          : `var(--org-secondary-color, rgba(255,255,255,0.12))`};
        color: ${selected
          ? `var(--org-primary-contrast, ${hoverTextColor})`
          : defaultTextColor};
        margin-right: ${theme.spacing(spc.xxs)};
        transition:
          background-color 0.2s ease,
          color 0.2s ease;
      }

      :hover .letter-avatar {
        background-color: ${selected
          ? activeBgColor
          : `var(--org-secondary-color, ${hoverBgColor})`};
        color: ${selected
          ? `var(--org-primary-contrast, ${hoverTextColor})`
          : hoverTextColor};
      }

      .menu-item-body {
        min-width: 0;
        overflow: hidden;
      }

      .menu-item-content {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-align: left;
      }

      .right-icon {
        font-size: 24px !important;
        margin-left: auto;
        opacity: 0.7;
      }
    }
  `;
});

/**
 * Left Navigation Menu Item
 *
 * A Button component designed to be placed within `<LeftNavMenu/>`
 *
 * It inherits all properties of a Button, and can optionally display an icon.
 *
 * Can be used as a Link by passing component={Link} and to="..."
 * This avoids invalid HTML nesting of button inside anchor
 */
export const LeftNavMenuItem = React.forwardRef<HTMLElement, Props>(
  (props, ref) => {
    const t = useDesignTokens();
    const theme = useTheme();
    const { effectiveMode } = useThemeMode();
    const isDark = theme.palette.mode === 'dark' || effectiveMode === 'dark';
    return (
      <LeftNavMenuItemStyled
        ref={ref as React.Ref<HTMLButtonElement>}
        tokens={t}
        isDark={isDark}
        {...props}
      />
    );
  }
);
LeftNavMenuItem.displayName = 'LeftNavMenuItem';
